import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { AuthEntity } from '_entities';
import { URLS } from '_constants';

const { getAuthenticatedUser } = AuthEntity.selectors;

/**
 * A public route that a signed-in user has no business being on — the mirror of PrivateRoute.
 *
 * Used for the auth pages (login, forgot password): they are public, but reaching them with a live
 * session showed the login form wearing the whole authenticated shell — rail, search, avatar — because
 * every page renders inside PageContainer and only the session, not the route, decides what the nav
 * shows. Nothing was stale; the app simply let you sit on a login form while logged in.
 *
 * Applied per route via `redirectIfAuthenticated` in routes/public.js, so the genuinely public pages
 * (policy, snapshot maps, public retailer list) stay reachable while signed in — and so do the
 * password-reset pages, which must keep working when the email link is opened in a browser that is
 * already logged in.
 *
 * Safe against flicker: App does not mount the Router until the token check, profile fetch and
 * modularity-permissions call have settled, so `isAuthenticated` is final by the time this renders.
 */
function PublicOnlyRoute({ path, component, ...rest }) {
  const isAuthenticated = useSelector(getAuthenticatedUser);

  return isAuthenticated ? <Redirect to={URLS.dashboard} /> : <Route path={path} component={component} {...rest} />;
}

PublicOnlyRoute.propTypes = {
  // eslint-disable-next-line react/require-default-props
  path: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.array.isRequired]),
  component: PropTypes.elementType.isRequired,
};

export default PublicOnlyRoute;
